import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './UpdateSystemFullScreenModal.css';
import {
  selectMachineToUpdateVersionButton,
  setNotificationVisibility,
  setSystemUpdateFullScreenModal,
  showUpdateMachineVersionNotification,
} from '../../modules/actions/action';
import { connect } from 'react-redux';
import { ACTIVE_DEVICE } from '../../common/constant/common-constants';
import { getSystemVersionDetails } from '../../helper/SystemVersionDetailsHelper';
import { postMachineVersionUpdate } from '../../helper/MachineVersionUpdateHelper';
import { assignNx5Machine, fetchNx5Releases, getNx5OtaErrorMessage } from '../../helper/Nx5OtaHelper';

const NX5_CHANNELS = [
  { value: 'prod', label: 'Prod' },
  { value: 'test', label: 'Test' },
  { value: 'patch', label: 'Patch' },
];

const getVersionKey = (version) => `${version.softwareVersion}::${version.osVersion}`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 7l10 10M17 7 7 17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const UpdateSystemFullScreenModal = (props) => {
  const [dataContainer, setDataContainer] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionKey, setSelectedVersionKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [releases, setReleases] = useState([]);
  const [envType, setEnvType] = useState('Prod');
  const fetchedRef = useRef(false);
  const isNx5 = props.activeDevice === ACTIVE_DEVICE.NX5;

  const selectedMachines = props.selectedMachineToUpdateVersion || [];
  const machineCount = selectedMachines.length;

  const selectedVersion = useMemo(
    () => versions.find((version) => getVersionKey(version) === selectedVersionKey) || null,
    [versions, selectedVersionKey],
  );

  const handleModalClose = () => {
    props.closeModal();
  };

  const handleSystemVersionDataGet = useCallback(() => {
    setLoading(true);
    setError('');
    getSystemVersionDetails()
      .then((res) => {
        setDataContainer(res.data);
      })
      .catch(() => {
        setError('Unable to load system versions. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isNx5) {
      setEnvType('prod');
      setLoading(true);
      setError('');
      fetchNx5Releases()
        .then((data) => setReleases(Array.isArray(data?.releases) ? data.releases : []))
        .catch((err) => setError(getNx5OtaErrorMessage(err, 'Unable to load releases.')))
        .finally(() => setLoading(false));
      return;
    }
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      handleSystemVersionDataGet();
    }
  }, [handleSystemVersionDataGet, isNx5]);

  useEffect(() => {
    if (!dataContainer) {
      setVersions([]);
      return;
    }

    const nextVersions = envType === 'Prod' ? dataContainer.prod : dataContainer.test;
    setVersions(Array.isArray(nextVersions) ? nextVersions : []);
    setSelectedVersionKey('');
  }, [dataContainer, envType]);

  const visibleReleases = releases.filter((item) => item.channel === envType);
  const selectedRelease = visibleReleases.find((item) => `${item.channel}:${item.release}` === selectedVersionKey) || null;

  const handleMachineUpdateVersion = () => {
    if (machineCount === 0 || submitting) return;
    if (isNx5) {
      if (!selectedRelease) return;
      setSubmitting(true);
      Promise.all(selectedMachines.map((machineUDI) => assignNx5Machine({
        device: machineUDI,
        release: selectedRelease.release,
        channel: selectedRelease.channel,
      })))
        .then(() => {
          props.showMachineUpdateNotification();
          props.openNotification();
          props.closeModal();
        })
        .catch((err) => {
          setError(getNx5OtaErrorMessage(err, 'Failed to assign the release.'));
        })
        .finally(() => setSubmitting(false));
      return;
    }
    if (!selectedVersion) return;

    const dataArray = selectedMachines.map((machineUDI) => ({
      machineUDI,
      newSoftwareVersion: selectedVersion.softwareVersion,
      newOSVersion: selectedVersion.osVersion,
    }));

    setSubmitting(true);
    postMachineVersionUpdate(JSON.stringify({ data: dataArray }))
      .then(() => {
        props.showMachineUpdateNotification();
        props.openNotification();
        props.closeModal();
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Failed to send update.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="sys-update-modal-overlay" role="presentation" onClick={handleModalClose}>
      <div
        className="sys-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sys-update-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sys-update-modal-header">
          <div className="sys-update-modal-header-text">
            <h2 id="sys-update-modal-title" className="sys-update-modal-title">Select System Version</h2>
            <p className="sys-update-modal-subtitle">
              {machineCount === 0
                ? 'No machines selected'
                : `Sending update to ${machineCount} machine${machineCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            className="sys-update-modal-close"
            onClick={handleModalClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="sys-update-modal-env-toggle">
          {(isNx5 ? NX5_CHANNELS : [
            { value: 'Prod', label: 'Prod' },
            { value: 'Test', label: 'Dev' },
          ]).map((channel) => (
            <button
              key={channel.value}
              type="button"
              className={`sys-update-env-btn${envType === channel.value ? ' sys-update-env-btn--active' : ''}`}
              onClick={() => {
                setEnvType(channel.value);
                setSelectedVersionKey('');
              }}
            >
              {channel.label}
            </button>
          ))}
        </div>

        <div className="sys-update-modal-body">
          {loading && (
            <div className="sys-update-modal-state">
              <div className="sys-update-modal-spinner" />
              <p>Loading versions…</p>
            </div>
          )}

          {!loading && error && (
            <div className="sys-update-modal-state sys-update-modal-state--error">
              <p>{error}</p>
              <button type="button" onClick={handleSystemVersionDataGet}>Try again</button>
            </div>
          )}

          {!loading && !error && (isNx5 ? visibleReleases.length === 0 : versions.length === 0) && (
            <div className="sys-update-modal-state">
              <p>No {isNx5 ? 'releases' : 'versions'} available for {isNx5 ? (NX5_CHANNELS.find((item) => item.value === envType)?.label || envType) : envType}.</p>
            </div>
          )}

          {!loading && !error && isNx5 && visibleReleases.length > 0 && (
            <div className="sys-update-version-list">
              {visibleReleases.map((item) => {
                const versionKey = `${item.channel}:${item.release}`;
                const isSelected = selectedVersionKey === versionKey;
                const files = (item.components || []).map((component) => component.name).join(', ');
                return (
                  <button
                    key={versionKey}
                    type="button"
                    className={`sys-update-version-card${isSelected ? ' sys-update-version-card--selected' : ''}`}
                    onClick={() => setSelectedVersionKey(versionKey)}
                    aria-pressed={isSelected}
                  >
                    <span className="sys-update-version-radio" aria-hidden="true">
                      <span className="sys-update-version-radio-dot" />
                    </span>
                    <div className="sys-update-version-main">
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">RELEASE</span>
                        <span className="sys-update-version-value">{item.release}</span>
                      </div>
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">FILES</span>
                        <span className="sys-update-version-value">{files || '—'}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && !error && !isNx5 && versions.length > 0 && (
            <div className="sys-update-version-list">
              {versions.map((sysVersion, index) => {
                const versionKey = getVersionKey(sysVersion);
                const isSelected = selectedVersionKey === versionKey;

                return (
                  <button
                    key={`${versionKey}-${index}`}
                    type="button"
                    className={`sys-update-version-card${isSelected ? ' sys-update-version-card--selected' : ''}`}
                    onClick={() => setSelectedVersionKey(versionKey)}
                    aria-pressed={isSelected}
                  >
                    <span className="sys-update-version-radio" aria-hidden="true">
                      <span className="sys-update-version-radio-dot" />
                    </span>

                    <div className="sys-update-version-main">
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">SYSTEM</span>
                        <span className="sys-update-version-value">{sysVersion.softwareVersion || '—'}</span>
                      </div>
                      <div className="sys-update-version-row">
                        <span className="sys-update-version-label">OS</span>
                        <span className="sys-update-version-value">{sysVersion.osVersion || '—'}</span>
                      </div>
                      {sysVersion.description && (
                        <p className="sys-update-version-description">{sysVersion.description}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="sys-update-modal-footer">
          <button type="button" className="sys-update-btn sys-update-btn--ghost" onClick={handleModalClose}>
            Cancel
          </button>
          <button
            type="button"
            className="sys-update-btn sys-update-btn--primary"
            onClick={handleMachineUpdateVersion}
            disabled={(isNx5 ? !selectedRelease : !selectedVersion) || machineCount === 0 || submitting}
          >
            {submitting ? 'Sending…' : 'Send Update'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  showModal: state.systemUpdateFullScreenModal.showSystemUpdateModal,
  showNotification: state.showNotification.showNotificatin,
  selectedMachineToUpdateVersion: state.selectMachineToUpdateVersion.selectedMachineToUpdateVersion,
  activeDevice: state.setactiveDevice.device,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: () => { dispatch(setSystemUpdateFullScreenModal(true)); },
  closeModal: () => { dispatch(setSystemUpdateFullScreenModal(false)); },
  openNotification: () => { dispatch(setNotificationVisibility(true)); },
  closeNotification: () => { dispatch(setNotificationVisibility(false)); },
  selectMachineToUpdateVersion: () => { dispatch(selectMachineToUpdateVersionButton()); },
  showMachineUpdateNotification: () => { dispatch(showUpdateMachineVersionNotification(true)); },
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateSystemFullScreenModal);
